import React, { useMemo, useCallback } from "react";
import { Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import CheckboxIcon from "@/assets/icons/checkbox.svg?react";
import InfoTooltip from "@/components/UI/InfoTooltip";
import StatusTransition from "@/components/UI/StatusTransition";
import { getTime, groupNotificationsByDateAndType } from "@/utils/dateUtils";
import CalendarFilter from "./CalendarFilter";
import SentenceCreator from "./SentenceCreator";
import { useCalendarFilter } from "@/hooks/useCalendarFilter";
import { useNotifications } from "@/hooks/useNotifications";
import { useScopeParams } from "@/hooks/useScopeParams";
import Loader from "../UI/Loader";
import type { Notification } from "./notifications";
import dayjs from "dayjs";

type NotificationItemProps = {
  notification: Notification;
  onMarkRead: (id: string) => void;
};

export default function NotificationsHome() {
  const { id } = useScopeParams();

  const {
    allNotifications,
    isLoading,
    error,
    refetch,
    markAsRead,
    markAllAsRead,
    markAllAsReadLoading,
  } = useNotifications(id, {
    mode: "all",
    refetchInterval: 30 * 60 * 1000,
    enabled: !!id,
  });

  const calendarFilter = useCalendarFilter();

  const filterNotifications = useCallback(
    (notifications: Notification[]) => {
      if (!notifications) return [];

      return notifications.filter((notification) => {
        const updatedDate = notification.notification_data?.at;
        if (!updatedDate) return false;

        const date = dayjs(updatedDate);

        switch (calendarFilter.appliedValue) {
          case "on": {
            const target = calendarFilter.appliedDates;
            if (!target || Array.isArray(target)) return true;
            return date.isSame(dayjs(target), "day");
          }

          case "before": {
            const target = calendarFilter.appliedDates;
            if (!target || Array.isArray(target)) return true;
            return date.isBefore(dayjs(target), "day");
          }

          case "after": {
            const target = calendarFilter.appliedDates;
            if (!target || Array.isArray(target)) return true;
            return date.isAfter(dayjs(target), "day");
          }

          case "between": {
            const dates = calendarFilter.appliedDates;
            if (!dates || !Array.isArray(dates)) return true;
            const [start, end] = dates;
            if (!start || !end) return true;

            const startDay = dayjs(start).startOf("day");
            const endDay = dayjs(end).endOf("day");

            const onOrAfterStart =
              date.isAfter(startDay) || date.isSame(startDay, "day");
            const onOrBeforeEnd =
              date.isBefore(endDay) || date.isSame(endDay, "day");

            return onOrAfterStart && onOrBeforeEnd;
          }

          default:
            return true;
        }
      });
    },
    [calendarFilter.appliedDates, calendarFilter.appliedValue],
  );

  const filteredNotifications = useMemo(() => {
    return filterNotifications(allNotifications as Notification[]);
  }, [allNotifications, filterNotifications]);

  const groupedNotifications = useMemo(() => {
    return groupNotificationsByDateAndType(
      filteredNotifications as Array<{
        notification_data?: { at?: string | number | Date };
        type: string;
      }>,
    );
  }, [filteredNotifications]);

  const allRead = useMemo(() => {
    return filteredNotifications.every(
      (notification: Notification) => notification.is_read,
    );
  }, [filteredNotifications]);

  const onMarkRead = (notificationId: string) => {
    markAsRead(notificationId);
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  const handleClearAll = () => {
    calendarFilter.onClear();
    refetch();
  };

  if (error) {
    return (
      <div className="m-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-red-500 mb-4">Failed to load notifications</p>
            <Button onClick={refetch} type="primary">
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="m-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 mb-[18px]">
          <h1 className="text-[18px] font-semibold">
            All System Notifications
            <InfoTooltip
              title="View all system notifications (read and unread) from all time."
              data-testid="all-system-notifications-tooltip"
            />
          </h1>
        </div>
        <Button
          type="default"
          className="bg-transparent text-[#2B4752] border-[#2B4752] rounded-[4px]"
          onClick={handleMarkAllRead}
          disabled={allRead || markAllAsReadLoading}
          loading={markAllAsReadLoading}
          data-testid="notifications-mark-all-read-button"
        >
          Mark all as read
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <CalendarFilter calendarFilter={calendarFilter} />

        <Button
          type="text"
          className="p-0"
          icon={
            <CloseOutlined style={{ fontSize: "12px", color: "#2B4752" }} />
          }
          onClick={handleClearAll}
          data-testid="notification-clear-all-filters-button"
        >
          <span className="hide-on-small text-[#2B4752]">
            Clear all filters
          </span>
        </Button>
      </div>

      <div className="w-full mt-8">
        {isLoading && Object.entries(groupedNotifications).length === 0 ? (
          <div className="flex items-center justify-center h-96">
            <Loader />
          </div>
        ) : Object.entries(groupedNotifications).length === 0 ? (
          <div className="flex items-center justify-center h-96">
            <p className="text-center text-gray-500">
              No notifications to display.
            </p>
          </div>
        ) : (
          Object.entries(groupedNotifications).map(([date, types]) => (
            <div
              key={date}
              className="mb-2"
              data-testid="system-notification-date-group"
            >
              <h2 className="text-[#00000073] text-base mb-3 font-semibold">
                {date}
              </h2>
              {Object.entries(types).map(([type, notifications]) => (
                <div key={type}>
                  {(notifications as Notification[]).map(
                    (notification: Notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkRead={onMarkRead}
                      />
                    ),
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const NotificationItem = ({
  notification,
  onMarkRead,
}: NotificationItemProps) => {
  const { id } = useScopeParams();
  const { markReadMutation } = useNotifications(id);

  if (!notification.notification_data) {
    return <div className="text-red-500">Invalid notification data</div>;
  }

  const timestamp = getTime(notification.notification_data.at);
  const isRead = notification.is_read;
  return (
    <div className="flex flex-col" data-testid="system-notification-card">
      <div className="flex flex-col gap-2 border border-[#0000001f] bg-white mb-4 rounded-lg shadow-xs">
        <div className="flex flex-col gap- hover:bg-[#00000005] relative group p-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[#00000073] text-sm font-medium">{timestamp}</p>
            {!isRead && (
              <span
                className="h-[10px] w-[10px] bg-[#0C98D0] rounded-full group-hover:opacity-0 transition-opacity duration-200"
                data-testid="notification-unread-indicator"
              ></span>
            )}
          </div>
          <div className="flex items-center">
            {!isRead && (
              <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity px-1 py-1">
                <div className="flex items-center gap-1 ml-1 mr-1">
                  <Button
                    type="default"
                    onClick={() => onMarkRead(notification.id)}
                    className="text-[#556C75] text-xs hover:text-blue-400 px-3 py-0 h-[20px] rounded-none"
                    icon={
                      <CheckboxIcon className="text-[#556C75] w-[14px] h-[14px] mt-[2px]" />
                    }
                    loading={markReadMutation.isPending}
                    data-testid="notification-mark-read-button"
                  >
                    Mark read
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="mb-2">
            <SentenceCreator
              notification={notification}
              type={notification.notification_data.field}
            />
          </div>
          {notification.notification_data.field && (
            <div>
              <StatusTransition
                type={notification.notification_data.field}
                fromValue={notification.notification_data.from_value}
                toValue={notification.notification_data.to_value}
                avatarConfig={{
                  avatarSize: 25,
                  avatarFontSize: 10,
                  labelFontSize: 14,
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
